import { marginHorizontal, paddingHorizontal } from "@gadgetinc/widgets";
import { styled } from "baseui";
import { useNavigationDetails } from "fastify-renderer/client/react";
import { omit } from "lodash";
import type { ComponentProps, ReactNode } from "react";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "wouter";
import { DocsFeedback } from "../components/DocsFeedback";
import { extractMDXHeadings } from "../utils/extractMDXHeadings";
import { DocsInterface } from "./DocsInterface";
import { DocsPageHeaderSelectors } from "./DocsPageHeaderSelectors";
import { MismatchedDocsVersionBanner } from "./MismatchedDocsVersionBanner";
import { DocsPageTableOfContents, OnlyShowOnThreeColumnNav } from "./nav/DocsPageTableOfContents";

export const CenterContentLane = styled<{ $isHidden?: boolean; $tableOfContents: boolean }, "div">(
  "div",
  ({ $theme, $isHidden, $tableOfContents }) => {
    return {
      width: "100%",
      minWidth: "0px",
      maxWidth: `calc(min(100%, ${$tableOfContents ? "1128px" : "800px"}))`,
      backgroundColor: $theme.colors.white,
      opacity: $isHidden ? 0 : 1,
      transition: `opacity ${$theme.animation.timing200}`,
      ...marginHorizontal($theme.sizing.scale200),
      "@media screen and (min-width: 320px)": {
        ...marginHorizontal("auto"),
        ...paddingHorizontal($theme.sizing.scale800),
      },
    };
  }
);

export const BodyContentGrid = styled("div", ({ $theme }) => {
  return {
    display: "flex",
    flexDirection: "row",
  };
});

export const BodyContentContainer = styled("main", ({ $theme }) => {
  return {
    display: "block",
    minWidth: "300px",
    maxWidth: "800px",
  };
});

/** Used by all the markdown pages to apply an automatic layout */
export const DocsContentContainer = (
  props: ComponentProps<typeof DocsInterface> & {
    tableOfContents?: boolean;
  }
) => {
  const [location] = useLocation();
  const [isNavigating, destination] = useNavigationDetails();
  const [isLoading, setIsLoading] = useState(true);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const oldPath = location.split("#", 2)[0];
  const [newPath, newHash] = (destination ?? "/").split("#", 2);
  const navigationIsOnPage = oldPath == newPath;

  // Scroll to top whenever we navigate to a new page
  useEffect(() => {
    if (navigationIsOnPage) return;
    if (timer.current) clearTimeout(timer.current);
    setIsLoading(true);

    // Only scroll to top if the user isn't targeting a specific heading
    if (!newHash) {
      // Only scroll after we fade out, for a smooth (but quick) experience
      setTimeout(() => {
        window?.scrollTo(0, 0);
      }, 210);
    }
  }, [destination, navigationIsOnPage, newHash]);

  // Only fade in after we've finished navigating to the new page (with a short delay to alow for rendering)
  useEffect(() => {
    if (isLoading && !isNavigating) {
      timer.current = setTimeout(() => {
        setIsLoading(false);
      }, 200);
    }
  }, [isLoading, isNavigating]);

  // Clean-up timer, if the component unmounts while we're transitioning
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );

  let tableOfContents: ReactNode | null = null;
  if (props.tableOfContents ?? true) {
    const headings = extractMDXHeadings(props.children, 4);
    if (headings.length > 0) {
      tableOfContents = <DocsPageTableOfContents headings={headings} />;
    }
  }

  return (
    <>
      {props.title && (
        <Helmet>
          <title>{props.title}</title>
          <meta name="gadget:pagetitle" content={props.pageTitle} />
          <meta name="description" content={props.pageTitle ? `${props.pageTitle} - Gadget Docs` : "Gadget Docs"} />
        </Helmet>
      )}
      <DocsInterface {...omit(props, ["children"])} showNavDrawerButton>
        <DocsPageHeaderSelectors showInitially={!newHash} />
        <CenterContentLane
          data-testid="center-content-lane"
          $isHidden={isLoading || (isNavigating && !navigationIsOnPage)}
          $tableOfContents={!!tableOfContents}
        >
          <MismatchedDocsVersionBanner />
          <BodyContentGrid>
            <BodyContentContainer>{props.children}</BodyContentContainer>
            {tableOfContents}
          </BodyContentGrid>

          <OnlyShowOnThreeColumnNav $style={{ marginTop: "32px" }}>
            <DocsFeedback />
          </OnlyShowOnThreeColumnNav>
        </CenterContentLane>
      </DocsInterface>
    </>
  );
};
